/**
 * Lesson Audio Generation — fan-out stage.
 * Synthesizes audio from transcripts/dialogue in the lesson content (OpenAI or Fish).
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { LessonStagePanel } from '../../shared/LessonStagePanel';
import { useLessonContext } from '../../LessonContext';
import { useLessonBatchExecution } from '../../shared/useLessonBatchExecution';
import { EmptyStageNotice } from '../../shared/EmptyStageNotice';
import { AudioSpecEditor } from './AudioSpecEditor';

function collectAudioSpecs(lessonContent: any): Array<{ audio_id: string; section_title: string; description: string; transcript: string }> {
  const specs: any[] = [];
  for (const section of lessonContent?.sections || []) {
    for (const item of section.content || []) {
      if (item.type === 'audio') {
        specs.push({
          audio_id: `aud_${specs.length}`,
          section_title: section.title || '(Opening)',
          description: item.description || '',
          transcript: item.transcript || '',
        });
      }
    }
  }
  return specs;
}

function AudioCards({ onSelectedIdChange }: { onSelectedIdChange: (id: string | null) => void }) {
  const { document } = useLessonContext();
  const lessonContent: any = document?.lesson_content;
  const audioItems: any = document?.audio || {};
  const specs = collectAudioSpecs(lessonContent);

  // TTS provider — default to whatever this lesson's existing audio used, else the
  // last session's choice, else OpenAI. Stops auto-deriving once the user picks one.
  const [provider, setProvider] = useState<'openai' | 'fish'>(
    () => {
      try { const v = localStorage.getItem('tts_provider'); if (v === 'fish' || v === 'openai') return v; } catch { /* ignore */ }
      return 'openai';
    },
  );
  const userSetProvider = useRef(false);
  useEffect(() => {
    if (userSetProvider.current) return;
    const items = (document?.audio || {}) as Record<string, { provider?: string }>;
    const existing = Object.values(items).find((a) => a?.provider)?.provider;
    if (existing === 'fish' || existing === 'openai') setProvider(existing);
  }, [document]);
  const handleProviderChange = (p: 'openai' | 'fish') => {
    userSetProvider.current = true;
    try { localStorage.setItem('tts_provider', p); } catch { /* ignore */ }
    setProvider(p);
  };
  const providerRef = useRef(provider);
  providerRef.current = provider;
  const getExtraParams = useCallback(
    () => (providerRef.current === 'fish' ? { ttsProvider: 'fish', ttsModel: 's2-pro' } : {}),
    [],
  );

  const {
    selectedId, setSelectedId: setSelectedIdInternal,
    runningAll, batchStatus, runningSingle,
    runSingle, runAll, cancelBatch,
  } = useLessonBatchExecution({ stageId: 'lesson_audio_gen', getExtraParams });

  const setSelectedId = React.useCallback((id: string | null) => {
    setSelectedIdInternal(id);
    onSelectedIdChange(id);
  }, [setSelectedIdInternal, onSelectedIdChange]);

  const selectedAudio = selectedId ? audioItems[selectedId] : null;

  return (
    <div className="space-y-3">
      {/* Batch controls */}
      <div className="flex items-center gap-2">
        <label className="flex items-center gap-1 text-xs text-gray-600">
          <span className="font-medium">Voice engine</span>
          <select
            className="border border-gray-300 rounded px-2 py-1 text-xs disabled:opacity-50"
            value={provider}
            disabled={runningAll || runningSingle}
            onChange={(e) => handleProviderChange(e.target.value as 'openai' | 'fish')}
          >
            <option value="openai">OpenAI (gpt-4o-mini-tts)</option>
            <option value="fish">Fish Audio (s2-pro)</option>
          </select>
        </label>
        <button
          type="button"
          className="px-3 py-1.5 text-sm rounded-md text-white hover:opacity-90 disabled:opacity-50"
          style={{ backgroundColor: '#1a2332' }}
          disabled={runningAll || specs.length === 0}
          onClick={() => runAll(specs.map((s) => s.audio_id))}
        >
          {runningAll ? 'Generating All...' : `Generate All (${specs.length})`}
        </button>
        {runningAll && (
          <button type="button" className="px-3 py-1.5 text-sm rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50" onClick={cancelBatch}>Cancel</button>
        )}
        <span className="text-xs text-gray-400">
          {Math.max(
            Object.keys(audioItems).length,
            Object.values(batchStatus).filter((s: any) => s.status === 'succeeded').length
          )}/{specs.length} generated
        </span>
      </div>

      {/* Audio spec cards */}
      <div className="flex gap-2 overflow-x-auto pb-2">
        {specs.map((spec) => {
          const hasAudio = !!audioItems[spec.audio_id];
          const batch = batchStatus[spec.audio_id];
          return (
            <button
              key={spec.audio_id}
              type="button"
              className={`flex-shrink-0 border rounded px-3 py-2 text-left text-xs w-52 ${
                selectedId === spec.audio_id ? 'ring-2 ring-blue-500 border-blue-300' : 'border-gray-200'
              } ${hasAudio ? 'bg-green-50' : 'bg-white'}`}
              onClick={() => setSelectedId(spec.audio_id)}
            >
              <div className="font-medium text-gray-800 truncate">{spec.section_title}</div>
              <div className="text-gray-500 mt-0.5 truncate">{spec.description.slice(0, 60)}...</div>
              {batch && (
                <div className={`text-[10px] mt-1 ${batch.status === 'succeeded' ? 'text-green-600' : batch.status === 'failed' ? 'text-red-600' : 'text-blue-600'}`}>
                  {batch.status}
                </div>
              )}
              {!batch && hasAudio && (
                <div className="text-[10px] mt-1 text-green-600">generated</div>
              )}
            </button>
          );
        })}
      </div>

      {/* Selected audio editor */}
      {selectedId && (
        <AudioSpecEditor
          key={selectedId}
          audioId={selectedId}
          provider={provider}
          generatedAudio={selectedAudio}
          generating={runningSingle || runningAll}
          onGenerate={runSingle}
        />
      )}
    </div>
  );
}

function renderOutput(doc: Record<string, unknown>) {
  const audio = (doc.audio as any) || {};
  const count = Object.keys(audio).length;
  if (count === 0) return <div className="text-sm text-gray-500">No audio generated yet.</div>;
  return <div className="text-xs text-gray-600">{count} audio clips generated</div>;
}

export function LessonAudioGen() {
  const { document } = useLessonContext();
  const lessonContent = document?.lesson_content as any;
  const hasLessonContent = !!lessonContent?.sections?.length;
  const specs = hasLessonContent ? collectAudioSpecs(lessonContent) : [];
  const [extractionId, setExtractionId] = React.useState<string | null>(null);

  if (hasLessonContent && specs.length === 0) {
    return <EmptyStageNotice stageId="lesson_audio_gen" contentType="audio" />;
  }

  return (
    <LessonStagePanel
      stageId="lesson_audio_gen"
      title="Audio Generation"
      description="Synthesize audio from transcripts in the lesson content. Pick the voice engine (OpenAI or Fish Audio) before generating."
      hideRunButton
      renderOutput={renderOutput}
      extractionId={extractionId || undefined}
    >
      {hasLessonContent ? <AudioCards onSelectedIdChange={setExtractionId} /> : (
        <div className="text-xs text-gray-500">No lesson content. Generate and review the lesson first.</div>
      )}
    </LessonStagePanel>
  );
}
